import React, { act, useEffect, useMemo, useState } from 'react';
import {useApiData} from '../../contexts/apiService';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { getFirestore, doc, getDoc,collection, getDocs } from 'firebase/firestore';
import '../Investigations/investigations.css';
import LoaderWheel from '../../components/common/LoaderWheel';
import Calculators from './calculators';
import Policies from './policies';
import Investigations from './investigations';
import Audits from './audits';
import PricingAudit from './PricingAudit';
import LabResults from './labResults';
import { useUserAccess, USER_ROLE_LEVEL } from '../../contexts/userAccess';
interface CarrierContact {
    name: string;
    id: string;
}
interface userItem {
    name: string;
    id: string;
    company: string;
    email: string;
}
interface ProducerContact {
    name: string;
    id: string;
}

interface IncidentClassification {
    description: string;
    id: string;
    auditPoints: string;
}
interface dropdownItem{
    data: unknown[];
}
interface IncidentRowData {
    [key: string]: any;
}

const MemoizedCalculators = React.memo(Calculators);
const MemoizedPolicies = React.memo(Policies);

export default function AuditTable() {
    const [hasAdminAccess, setHasAdminAccess] = useState<boolean>(false);
    const [activeUser, setActiveUser] = useState<string>('');
    const {  isLoading } = useApiData();
    const [activeScreen, setActiveScreen] = useState<string>(()=>{
        if(hasAdminAccess){
            return 'auditTable'
        }else{
            return 'calculators'
        }
        }
    );
    
    const [allRockbridgeUsers, setAllRockbridgeUsers] = useState<userItem[]>([]);
    const { userRole = 'viewer' } = useUserAccess();
    const SCHEDULE_ADMIN_LEVEL = USER_ROLE_LEVEL['schedule_admin'];
    const showTable =  Number(USER_ROLE_LEVEL[userRole]) >= SCHEDULE_ADMIN_LEVEL;
    
    useEffect(() => {
        const auth = getAuth();
        const db = getFirestore();
        
       
        /**
         * Subscribes to authentication state changes and updates admin access state accordingly.
         * 
         * - When a user is authenticated, fetches the user's document from Firestore.
         * - Checks if the user has admin privileges (`role === 'admin'`) or explicit trigger access (`canAccessTriggers === true`).
         * - Updates the `hasAdminAccess` state based on the user's permissions.
         * - If the user loses admin access while on an admin-only table, triggers a redirect to a non-admin table (handled elsewhere).
         * - If the user logs out, resets the `hasAdminAccess` state to `false` and ensures the UI reflects non-admin access.
         * 
         * @returns {Unsubscribe} A function to unsubscribe from the authentication state listener.
         */
        
        const unsubscribe = onAuthStateChanged(auth, async (user) => {
            if (user) {
                const userDoc = await getDoc(doc(db, 'users', user.uid));
                if (userDoc.exists()) {
                    const userData = userDoc.data();
                    setActiveUser(userData.name);
                    setHasAdminAccess(userData.role === 'admin' || userData.canAccessTriggers === true); 
                    // If user loses admin access and is on an admin-only table, redirect to a non-admin table
                }
            } else {
                setHasAdminAccess(false);
                // Reset to non-admin table if logged out
            }
        });
        return () => unsubscribe();
        
    });
     

    useEffect(() => {
        async function fetchRockbridgeUsers() {
            const db = getFirestore();
            const usersCollection = collection(db, "users");
            const snapshot = await getDocs(usersCollection);
            const usersList = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }) as userItem);
            const rockbridgeUsers = usersList.filter(
                user => user.company && user.company.toLowerCase() === "rockbridge"
            );
            const mappedRockbridgeUsers = rockbridgeUsers.map(user => ({
                email: user.email,
                name: user.name,
                id: user.id
            }) as userItem);
            setAllRockbridgeUsers(mappedRockbridgeUsers);
        }
        fetchRockbridgeUsers();
    }, []);  
    
    
    //main function return
    // This component renders a tabbed interface for viewing audit tables and investigations.
    // It allows users to switch between the two views and displays the appropriate content based on the active screen.
    
    if (!showTable) {
        return <p>Not Allowed</p>;
    }
    if(isLoading){
        return(
            <div className='loading-container'>
                <LoaderWheel />
            </div>
        )
    }else{
        return(
            <div className='audit-table-container'>
                <div className="tab-selector">
                    {hasAdminAccess ? (
                        <>
                            <button className={`tab-selector-button${activeScreen === 'auditTable' ? ' active' : ''}`} onClick={()=>setActiveScreen('auditTable')}> 
                                Audit Table
                            </button>
                            <button className={`tab-selector-button${activeScreen === 'investigations' ? ' active' : ''}`} onClick={()=>setActiveScreen('investigations')}> 
                            Investigations
                            </button>
                            <button className={`tab-selector-button${activeScreen === 'pricingAudit' ? ' active' : ''}`} onClick={()=>setActiveScreen('pricingAudit')}> 
                                Pricing Audit
                            </button>
                        </>
                    ) : null}
                    <button className={`tab-selector-button${activeScreen === 'policies' ? ' active' : ''}`} onClick={()=>setActiveScreen('policies')}> 
                        Policies
                    </button>
                    
                    <button className={`tab-selector-button${activeScreen === 'calculators' ? ' active' : ''}`} onClick={()=>setActiveScreen('calculators')}>
                        Calculators
                    </button>
                    <button className={`tab-selector-button${activeScreen === 'labResults' ? ' active' : ''}`} onClick={()=>setActiveScreen('labResults')}>
                        Lab Results
                    </button>
                </div>


                {(() => {
                    switch (activeScreen) {
                        case 'auditTable':

                            if(hasAdminAccess){
                                return <Audits 
                                hasAdminAccess={hasAdminAccess}
                                activeUser={activeUser}
                                />;
                            }else{
                                return <div>You do not have access to view this content.</div>;
                            }
                        case 'investigations':
                            if(hasAdminAccess){
                            return <Investigations 
                                hasAdminAccess={hasAdminAccess}
                                activeUser={activeUser}
                            />;
                            }else{
                                return <div>You do not have access to view this content.</div>;
                            }

                        case 'pricingAudit':
                            if(hasAdminAccess){
                                return <PricingAudit />;
                            }else{
                                return <div>You do not have access to view this content.</div>;
                            }

                        case 'calculators':
                            return <MemoizedCalculators />;
                        case 'policies':
                            return <MemoizedPolicies
                                hasAdminAccess={hasAdminAccess}
                                activeUser={activeUser}
                            />;
                        case 'labResults':
                            return <LabResults />;
                        default:
                            return null;
                    }
                })()}

            </div>
        )
    }
        
    

}